import { memo, useRef, useState } from 'react'

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import type { IconDefinition } from '@fortawesome/free-solid-svg-icons'
import {
  faBolt,
  faDisplay,
  faPause,
  faPen,
  faPlay,
  faRotateLeft,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { cn } from '@/utils'

import { resolveSubTimer } from '~shared/rundown/sub-timer-run'
import type { SubTimerCueRun } from '~shared/rundown/sub-timer-run'

import { resetSubTimerRun, toggleSubTimerRun } from '@/entities/runner'

import type { SubTimer } from './sub-timers'
import { saveSubTimers } from './sub-timers.api'
import {
  SubTimerDragHandle,
  SubTimerDragHandleDisabled,
  SubTimerDragHandleStatic,
} from './sub-timers.dnd'
import { useSubTimerNow, useTicker } from './sub-timers.hooks'
import { useSubTimerRun } from './sub-timers.store'
import { getSubTimerView } from './sub-timers.view'
import { SUB_TIMER_POPOVER_CLASS, SubTimerForm } from './SubTimerForm'
import type { SubTimerFormHandle } from './SubTimerForm'

type SubTimerItemProps = {
  rundownId: string
  /** Null before the show starts: timers cannot run without a runner. */
  runnerId: string | null
  cueId: string
  /** Saved settings, from the cue. */
  settings: SubTimer
  cueRun: SubTimerCueRun | null
  /** Server minus local clock, in ms. Runs are stored in server time. */
  timeOffset: number
  /** Edit settings and reorder. */
  canEdit: boolean
  /** Start, pause and reset. */
  canRun: boolean
  /**
   * Reorder grip: live in a sortable list, disabled for a lone timer, drawn
   * only in the drag overlay. Omitted without `canEdit`.
   */
  handle?: 'drag' | 'disabled' | 'static'
}

const NO_RUNNER_TITLE = 'Start the show to run sub-timers'

/**
 * One sub timer.
 *
 * Memoised, and subscribed only to its own running state, so it re-renders
 * when its settings or state change, or every tick while it is running.
 */
export const SubTimerItem = memo(function SubTimerItem({
  rundownId,
  runnerId,
  cueId,
  settings,
  cueRun,
  timeOffset,
  canEdit,
  canRun,
  handle,
}: SubTimerItemProps) {
  const run = useSubTimerRun(settings.id)
  const [editOpen, setEditOpen] = useState(false)
  const formRef = useRef<SubTimerFormHandle>(null)
  const [pending, setPending] = useState(false)
  const { ref: tickerRef, start, stop } = useTicker<HTMLSpanElement>()

  const timer = resolveSubTimer(settings, run, cueRun)
  const now = useSubTimerNow(timer, timeOffset)

  const view = getSubTimerView(timer, now)
  const runnable = !!runnerId && !pending
  // The bar under the time holds the run controls and the reorder grip
  const hasBar = canEdit || canRun
  const controlTitle = runnerId ? undefined : NO_RUNNER_TITLE

  // The server resolves and writes the run; the change comes back through the
  // runner listener. Block repeat clicks until it lands.
  const act = (request: (runnerId: string) => Promise<unknown>) => {
    if (!runnerId) return
    setPending(true)
    void request(runnerId)
      .catch(() => {}) // toasted by the request handler
      .finally(() => setPending(false))
  }
  const target = (id: string) => ({
    rundownId,
    runnerId: id,
    cueId,
    timerId: settings.id,
  })
  const saveSettings = (update: (timers: SubTimer[]) => SubTimer[]) =>
    void saveSubTimers(rundownId, cueId, update)

  const triggerClass = cn(
    // Inset rings: the card clips anything drawn outside its edges
    'group/edit relative flex w-full flex-col items-center px-3.5 py-0.5 outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-white/60',
    hasBar ? 'rounded-t-sm' : 'rounded-sm',
    canEdit &&
      'cursor-pointer ring-inset hover:bg-white/10 hover:ring-1 hover:ring-white/30 data-popup-open:bg-white/10 data-popup-open:ring-1 data-popup-open:ring-white/50',
  )
  const triggerTitle = timer.name || (canEdit ? 'Edit timer' : undefined)
  const triggerContent = (
    <>
      {timer.name && (
        <span className="w-full overflow-hidden">
          <span
            ref={tickerRef}
            className="block truncate text-[10px] leading-tight text-white/70"
          >
            {timer.name}
          </span>
        </span>
      )}
      <span
        className={cn(
          'text-sm tabular-nums leading-tight',
          !view.running && 'text-white/70',
          view.over && 'text-red-500',
          view.finished && 'text-amber-400',
        )}
      >
        {view.label}
      </span>
      {timer.autoStart && (
        <FontAwesomeIcon
          icon={faBolt}
          title={
            timer.armed
              ? 'Starts with cue'
              : 'Starts with cue (taken over manually)'
          }
          className={cn(
            'absolute top-1 left-1 text-[8px]',
            timer.armed ? 'text-white/70' : 'text-white/25',
          )}
        />
      )}
      {timer.showOnOutput && (
        <FontAwesomeIcon
          icon={faDisplay}
          title="Shows on output"
          className={cn(
            'absolute top-1 right-1 text-[8px] text-white/70',
            // Makes way for the edit pencil on hover
            canEdit && 'group-hover/edit:opacity-0',
          )}
        />
      )}
      {canEdit && (
        <FontAwesomeIcon
          icon={faPen}
          className="absolute top-1 right-1 text-[8px] text-white/70 opacity-0 group-hover/edit:opacity-100"
        />
      )}
    </>
  )

  const hoverBtn =
    'w-1/4 flex-none text-[10px] opacity-0 group-hover/timer:opacity-100 focus-visible:opacity-100'

  return (
    <div
      className="group/timer flex flex-col rounded-sm bg-black/40"
      onMouseEnter={start}
      onMouseLeave={stop}
    >
      <div
        className={cn(
          'relative overflow-hidden',
          hasBar ? 'rounded-t-sm' : 'rounded-sm',
        )}
      >
        {view.progress !== null && (
          <div
            className={cn(
              'absolute inset-y-0 left-0 transition-[width] duration-300',
              view.over ? 'bg-red-600/40' : 'bg-white/15',
            )}
            style={{ width: `${view.progress * 100}%` }}
          />
        )}
        {/* Popover mounts only while open: idle rows skip its hooks */}
        {editOpen ? (
          <Popover
            open
            onOpenChange={(open, { reason }) => {
              // Closing saves, as popovers do elsewhere; Escape discards
              if (!open && reason !== 'escape-key') formRef.current?.commit()
              setEditOpen(open)
            }}
          >
            <PopoverTrigger
              title={triggerTitle}
              className={triggerClass}
            >
              {triggerContent}
            </PopoverTrigger>
            <PopoverContent className={SUB_TIMER_POPOVER_CLASS}>
              <SubTimerForm
                ref={formRef}
                title="Sub Timer"
                initial={settings}
                submitLabel="Save"
                onCancel={() => setEditOpen(false)}
                onSubmit={(input) => {
                  saveSettings((list) =>
                    list.map((t) =>
                      t.id === settings.id ? { ...input, id: t.id } : t,
                    ),
                  )
                  setEditOpen(false)
                }}
                onDelete={() => {
                  setEditOpen(false)
                  saveSettings((list) =>
                    list.filter((t) => t.id !== settings.id),
                  )
                }}
              />
            </PopoverContent>
          </Popover>
        ) : (
          <button
            type="button"
            disabled={!canEdit}
            title={triggerTitle}
            className={triggerClass}
            onClick={() => setEditOpen(true)}
          >
            {triggerContent}
          </button>
        )}
      </div>
      {hasBar && (
        <div className="flex h-4 rounded-b-sm bg-black/50">
          {canRun && (
            <>
              <IconButton
                icon={view.running ? faPause : faPlay}
                label={view.running ? 'Pause' : 'Start'}
                title={controlTitle}
                disabled={!runnable}
                onClick={() => act((id) => toggleSubTimerRun(target(id)))}
                className={cn(
                  'w-1/4 flex-none text-[10px]',
                  view.running && 'text-white',
                )}
              />
              <IconButton
                icon={faRotateLeft}
                label="Reset"
                title={controlTitle}
                disabled={!runnable}
                onClick={() => act((id) => resetSubTimerRun(target(id)))}
                className={hoverBtn}
              />
            </>
          )}
          {/* Fixed quarters: play, reset, a gap, then the grip on the right */}
          <span className="flex-1" />
          {handle === 'drag' && <SubTimerDragHandle timerId={settings.id} />}
          {handle === 'disabled' && <SubTimerDragHandleDisabled />}
          {handle === 'static' && <SubTimerDragHandleStatic />}
        </div>
      )}
    </div>
  )
})

type IconButtonProps = {
  icon: IconDefinition
  label: string
  /** Tooltip, when it should say more than the label. */
  title?: string
  disabled?: boolean
  onClick: () => void
  className?: string
}

function IconButton({
  icon,
  label,
  title,
  disabled,
  onClick,
  className,
}: IconButtonProps) {
  return (
    <button
      type="button"
      aria-label={label}
      title={title ?? label}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex items-center justify-center rounded-sm text-white/60 enabled:hover:bg-white/10 enabled:hover:text-white outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-white/60 disabled:cursor-not-allowed disabled:opacity-40',
        className,
      )}
    >
      <FontAwesomeIcon icon={icon} />
    </button>
  )
}
